<template>
  <div>
    <!-- Page header -->
    <CCard class="mb-4">
      <CCardBody class="p-4">
        <CRow class="align-items-center">
          <CCol md="7">
            <h1 class="h4 mb-1 font-weight-bold">Tenants</h1>
            <p class="text-muted mb-0">Every organisation using Niagaku, with its businesses and users</p>
          </CCol>
          <CCol md="5" class="d-flex justify-content-md-end mt-3 mt-md-0">
            <CButton
              color="light"
              class="mr-2"
              aria-label="Refresh"
              :disabled="loading"
              @click="refreshTable"
            >
              <CIcon name="cil-reload" />
            </CButton>
            <CButton color="primary" @click="addNew">
              <CIcon name="cil-plus" /> Add tenant
            </CButton>
          </CCol>
        </CRow>
      </CCardBody>
    </CCard>

    <CCard>
      <CCardBody>
        <div v-if="loading && !items.length" class="text-center p-5">
          <CSpinner color="primary" />
        </div>
        <div v-else-if="loadError" class="text-center p-5">
          <CIcon name="cil-warning" size="xl" class="text-danger mb-3" />
          <h2 class="h6 font-weight-bold">Couldn't load tenants</h2>
          <p class="text-muted">Check your connection and try again.</p>
          <CButton color="primary" @click="refreshTable">Retry</CButton>
        </div>
        <div v-else-if="!items.length" class="text-center p-5">
          <CIcon name="cil-building" size="xl" class="text-muted mb-3" />
          <h2 class="h6 font-weight-bold">No tenants yet</h2>
          <p class="text-muted">Tenants appear here when an organisation signs up.</p>
          <CButton color="primary" @click="addNew">Add tenant</CButton>
        </div>
        <template v-else>
          <!-- Desktop table -->
          <div class="d-none d-lg-block">
            <CDataTable
              :items="computedItems"
              :fields="fields"
              column-filter
              items-per-page-select
              :items-per-page="25"
              hover
              sorter
              pagination
              :loading="loading"
            >
              <template #show_index="{ index }">
                <td class="py-2">{{ index + 1 }}</td>
              </template>
              <template #actions="{ item }">
                <td class="text-nowrap">
                  <CButton
                    color="light"
                    size="sm"
                    class="mr-1"
                    :aria-label="`Edit ${item.tenantName}`"
                    @click="onEdit(item)"
                  >
                    <CIcon name="cil-pencil" />
                  </CButton>
                  <CButton
                    color="light"
                    size="sm"
                    :aria-label="`Delete ${item.tenantName}`"
                    @click="showDeleteConfirmation(item)"
                  >
                    <CIcon name="cil-trash" class="text-danger" />
                  </CButton>
                </td>
              </template>
            </CDataTable>
          </div>

          <!-- Mobile card list -->
          <div class="d-lg-none">
            <div
              v-for="item in computedItems"
              :key="item.id"
              class="border rounded p-3 mb-2"
            >
              <div class="font-weight-bold">{{ item.tenantName }}</div>
              <div class="small text-muted">{{ item.tenantEmail }}</div>
              <div class="small text-muted">
                {{ item.totalBusiness }} {{ item.totalBusiness === 1 ? "business" : "businesses" }}
                · {{ item.totalUser }} {{ item.totalUser === 1 ? "user" : "users" }}
                <span v-if="item.subsriptionExpiry">· Expires {{ item.subsriptionExpiry }}</span>
              </div>
              <div class="mt-2">
                <CButton color="light" size="sm" class="mr-2" @click="onEdit(item)">
                  <CIcon name="cil-pencil" /> Edit
                </CButton>
                <CButton color="light" size="sm" @click="showDeleteConfirmation(item)">
                  <CIcon name="cil-trash" class="text-danger" /> Delete
                </CButton>
              </div>
            </div>
          </div>
        </template>
      </CCardBody>
    </CCard>

    <CModal
      :title="`Delete tenant ${tenantLabel(itemToDelete)}?`"
      :show.sync="warningModal"
      :close-on-backdrop="!deleting"
    >
      The tenant, its sign-in accounts and access to all its businesses will be removed.
      This can't be undone.
      <template #footer>
        <CButton color="light" :disabled="deleting" @click="warningModal = false">
          Keep it
        </CButton>
        <CButton color="danger" :disabled="deleting" @click="doDelete">
          Delete tenant
        </CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import TenantApi from "@/lib/tenantApi";
import { formatDate, formatDateTime } from "@/utils/datetime";

const fields = [
  { key: "show_index", label: "#", _style: "width:1%", sorter: false, filter: false },
  { key: "tenantName", label: "Tenant" },
  { key: "tenantEmail", label: "Email" },
  { key: "totalBusiness", label: "Businesses" },
  { key: "totalUser", label: "Users" },
  { key: "subsriptionType", label: "Subscription" },
  { key: "subsriptionExpiry", label: "Expires" },
  { key: "createdOn", label: "Created" },
  { key: "actions", label: "", _style: "width:1%", sorter: false, filter: false },
];

export default {
  name: "TenantList",
  data() {
    return {
      loading: true,
      loadError: false,
      deleting: false,
      items: [],
      fields,
      api: new TenantApi(),
      warningModal: false,
      itemToDelete: {},
    };
  },
  mounted() {
    this.refreshTable();
  },
  computed: {
    computedItems() {
      return this.items.map((item) => {
        return {
          ...item,
          totalUser: this.getTotalProfile(item),
          tenantName: this.getTenantName(item),
          tenantEmail: this.getTenantEmail(item),
          totalBusiness: this.getTotalBusiness(item),
          subsriptionExpiry: formatDate(item.subsriptionExpiry),
          createdOn: formatDateTime(item.createdOn),
        };
      });
    },
  },
  methods: {
    tenantLabel(item) {
      return (item && item.tenantAdmin && item.tenantAdmin.fullName) || "";
    },
    getTotalProfile(item) {
      return (item.profiles && item.profiles.length) || 0;
    },
    getTotalBusiness(item) {
      return (item.businesses && item.businesses.length) || 0;
    },
    getTenantName(item) {
      return (item.tenantAdmin && item.tenantAdmin.fullName) || "Not set";
    },
    getTenantEmail(item) {
      return (item.tenantAdmin && item.tenantAdmin.email) || "Not set";
    },
    refreshTable() {
      var self = this;
      self.loading = true;
      self.loadError = false;
      self.api
        .getList()
        .then((response) => {
          self.items = response.result || [];
        })
        .catch((error) => {
          console.error(error);
          self.loadError = true;
        })
        .finally(() => {
          self.loading = false;
        });
    },
    onEdit(item) {
      this.$router.push({ path: `/admins/tenant/${item.id}` });
    },
    doDelete() {
      var self = this;
      var tenantId = self.itemToDelete.id;
      self.deleting = true;
      auth
        .deleteByTenant(tenantId)
        .then(() => self.api.delete(tenantId))
        .then(() => {
          self.$toast.success("Tenant deleted");
          self.warningModal = false;
          self.itemToDelete = {};
          self.refreshTable();
        })
        .catch((error) => {
          console.error(error);
          const data = error && error.data;
          self.$toast.error("Couldn't delete the tenant. " + helper.getErrorMessage(data));
        })
        .finally(() => {
          self.deleting = false;
        });
    },
    showDeleteConfirmation(item) {
      this.itemToDelete = item;
      this.warningModal = true;
    },
    addNew() {
      this.$router.push({ path: "/admins/tenant" });
    },
  },
};
</script>
